import { useState, useEffect } from 'react'
import { Link, useLocation, useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Search, Loader2, AlertCircle, Database, ExternalLink, FlaskConical, Dna, Layers } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Badge } from '../components/ui/Badge'
import {
  useBlastSearch,
  useBlastResults,
  BlastMibigHit,
  BlastPdbHit,
  BlastSwissprotHit,
  BlastCddHit,
  BlastCddSite,
  AggregatedLigand,
  AggregatedChebi,
} from '../api/hooks'
import { getPdbUrl, getUniprotUrl, getNcbiCddUrl } from '../utils/externalLinks'

type DatabaseType = 'mibig' | 'pdbaa' | 'swissprot' | 'Cdd'

const DATABASE_OPTIONS = [
  { value: 'mibig', label: 'MIBiG', description: 'Biosynthetic gene clusters', icon: Dna },
  { value: 'pdbaa', label: 'PDB', description: 'Protein Data Bank structures', icon: Layers },
  { value: 'swissprot', label: 'SwissProt', description: 'Curated protein sequences', icon: Database },
  { value: 'Cdd', label: 'CDD', description: 'Conserved Domain Database', icon: FlaskConical },
] as const

const EVALUE_OPTIONS = [
  { value: '1e-50', label: '1.0e-50 (Most stringent)' },
  { value: '1e-10', label: '1.0e-10 (Default)' },
  { value: '1e-5', label: '1.0e-5' },
  { value: '0.01', label: '0.01' },
  { value: '1', label: '1.0' },
  { value: '10', label: '10.0 (Least stringent)' },
]

const MAX_SEQUENCES_OPTIONS = [
  { value: 10, label: '10' },
  { value: 50, label: '50 (Default)' },
  { value: 100, label: '100' },
  { value: 250, label: '250' },
  { value: 500, label: '500' },
]

export function BlastSearchPage() {
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const [sequence, setSequence] = useState('')
  const [database, setDatabase] = useState<DatabaseType>('mibig')
  const [evalue, setEvalue] = useState('1e-10')
  const [maxSequences, setMaxSequences] = useState(50)
  const [jobId, setJobId] = useState<string | null>(null)

  const blastMutation = useBlastSearch()
  const { data: results } = useBlastResults(jobId || '', !!jobId)

  // Restore job_id and query from URL/sessionStorage on back navigation
  useEffect(() => {
    const urlJobId = searchParams.get('job')
    if (urlJobId && !jobId) {
      setJobId(urlJobId)
      // Restore query and settings from sessionStorage
      const cachedQuery = sessionStorage.getItem(`blast:query:${urlJobId}`)
      const cachedDatabase = sessionStorage.getItem(`blast:database:${urlJobId}`)
      const cachedEvalue = sessionStorage.getItem(`blast:evalue:${urlJobId}`)
      const cachedMaxSeqs = sessionStorage.getItem(`blast:maxseqs:${urlJobId}`)
      if (cachedQuery) setSequence(cachedQuery)
      if (cachedDatabase) setDatabase(cachedDatabase as DatabaseType)
      if (cachedEvalue) setEvalue(cachedEvalue)
      if (cachedMaxSeqs) setMaxSequences(parseInt(cachedMaxSeqs))
    }
  }, [searchParams, jobId])

  // Handle pre-filled sequence from Command Palette navigation
  useEffect(() => {
    const state = location.state as { sequence?: string } | null
    if (state?.sequence) {
      setSequence(state.sequence)
      // Clear the state to prevent re-applying on page refresh
      window.history.replaceState({}, document.title)
    }
  }, [location.state])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!sequence.trim()) return

    try {
      const response = await blastMutation.mutateAsync({
        query: sequence.trim(),
        database,
        evalue: parseFloat(evalue),
        max_sequences: maxSequences,
      })
      setJobId(response.job_id)
      // Update URL with job_id for back navigation support
      setSearchParams({ job: response.job_id })
      // Cache query and settings in sessionStorage for back navigation
      sessionStorage.setItem(`blast:query:${response.job_id}`, sequence.trim())
      sessionStorage.setItem(`blast:database:${response.job_id}`, database)
      sessionStorage.setItem(`blast:evalue:${response.job_id}`, evalue)
      sessionStorage.setItem(`blast:maxseqs:${response.job_id}`, maxSequences.toString())
    } catch (err) {
      console.error('BLAST search failed:', err)
    }
  }

  const isJobRunning = results?.status === 'queued' || results?.status === 'processing'
  const isJobComplete = results?.status === 'complete'
  const isJobFailed = results?.status === 'failed'

  const exampleSequence = `>Example protein sequence
MSKGEELFTGVVPILVELDGDVNGHKFSVSGEGEGDATYGKLTLKFICTTGKLPVPWPTL
VTTLTYGVQCFSRYPDHMKQHDFFKSAMPEGYVQERTIFFKDDGNYKTRAEVKFEGDTLV
NRIELKGIDFKEDGNILGHKLEYNYNSHNVYIMADKQKNGIKVNFKIRHNIEDGSVQLAD
HYQQNTPIGDGPVLLPDNHYLSTQSALSKDPNEKRDHMVLLEFVTAAGITLGMDELYK`

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white">BLAST Search</h1>
        <p className="text-gray-400 mt-1">
          Search your protein sequence against MIBiG, PDB, SwissProt, and CDD databases
        </p>
      </div>

      {/* Search Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="w-5 h-5 text-accent-cyan" />
            Submit Sequence
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Sequence Input */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-300">
                  Protein Sequence (FASTA format)
                </label>
                <button
                  type="button"
                  onClick={() => setSequence(exampleSequence)}
                  className="text-xs text-accent-cyan hover:underline"
                >
                  Load example
                </button>
              </div>
              <textarea
                value={sequence}
                onChange={(e) => setSequence(e.target.value)}
                placeholder="Paste your protein sequence in FASTA format..."
                className="w-full h-48 p-4 rounded-lg bg-white/5 border border-white/10 text-white font-mono text-sm placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-accent-cyan/50 focus:border-accent-cyan/50 resize-none"
              />
            </div>

            {/* Database Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">
                Target Database
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {DATABASE_OPTIONS.map((db) => {
                  const Icon = db.icon
                  const isSelected = database === db.value
                  return (
                    <button
                      key={db.value}
                      type="button"
                      onClick={() => setDatabase(db.value as DatabaseType)}
                      className={`p-4 rounded-lg border transition-all text-left ${
                        isSelected
                          ? 'bg-accent-cyan/20 border-accent-cyan text-white'
                          : 'bg-white/5 border-white/10 text-gray-400 hover:border-white/20 hover:text-white'
                      }`}
                    >
                      <Icon className={`w-5 h-5 mb-2 ${isSelected ? 'text-accent-cyan' : ''}`} />
                      <div className="font-medium">{db.label}</div>
                      <div className="text-xs text-gray-500 mt-1">{db.description}</div>
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Options */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Expect Threshold (E-value)
                </label>
                <select
                  value={evalue}
                  onChange={(e) => setEvalue(e.target.value)}
                  className="w-full rounded-lg bg-white/5 border border-white/10 text-white px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-accent-cyan/50"
                >
                  {EVALUE_OPTIONS.map((opt) => (
                    <option
                      key={opt.value}
                      value={opt.value}
                      style={{ backgroundColor: '#1a1a24', color: '#f3f4f6' }}
                    >
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Max Target Sequences
                </label>
                <select
                  value={maxSequences}
                  onChange={(e) => setMaxSequences(Number(e.target.value))}
                  className="w-full rounded-lg bg-white/5 border border-white/10 text-white px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-accent-cyan/50"
                  disabled={database === 'Cdd'}
                >
                  {MAX_SEQUENCES_OPTIONS.map((opt) => (
                    <option
                      key={opt.value}
                      value={opt.value}
                      style={{ backgroundColor: '#1a1a24', color: '#f3f4f6' }}
                    >
                      {opt.label}
                    </option>
                  ))}
                </select>
                {database === 'Cdd' && (
                  <p className="text-xs text-gray-500 mt-1">Not applicable for CDD searches</p>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end">
              <Button
                type="submit"
                size="lg"
                isLoading={blastMutation.isPending}
                leftIcon={<Search className="w-5 h-5" />}
                disabled={!sequence.trim() || isJobRunning}
              >
                Run BLAST Search
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Job Status */}
      {jobId && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Database className="w-5 h-5 text-accent-purple" />
                  Search Results
                </CardTitle>
                <Badge
                  variant={
                    isJobComplete ? 'green' :
                    isJobFailed ? 'red' :
                    'amber'
                  }
                >
                  {isJobRunning && <Loader2 className="w-3 h-3 animate-spin mr-1" />}
                  {results?.status || 'Pending'}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              {isJobRunning && (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 className="w-12 h-12 animate-spin text-accent-cyan mb-4" />
                  <p className="text-gray-400">Running BLAST search...</p>
                  <p className="text-sm text-gray-500 mt-1">
                    This may take a few minutes depending on the database size.
                  </p>
                </div>
              )}

              {isJobFailed && (
                <div className="flex flex-col items-center justify-center py-12 text-red-400">
                  <AlertCircle className="w-12 h-12 mb-4" />
                  <p>Search failed</p>
                  <p className="text-sm text-gray-500 mt-1">
                    An unexpected error occurred. Please try again.
                  </p>
                </div>
              )}

              {isJobComplete && results && (
                <div className="space-y-4">
                  <p className="text-gray-400">
                    Found {results.total_hits} hits in {results.database} database
                    {results.query_length > 0 && ` (query length: ${results.query_length} aa)`}
                  </p>

                  {results.total_hits === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      No significant hits found. Try adjusting the E-value threshold.
                    </div>
                  ) : (
                    <>
                      {/* MIBiG Results */}
                      {results.database === 'mibig' && results.mibig_hits.length > 0 && (
                        <MibigResultsTable hits={results.mibig_hits} />
                      )}

                      {/* PDB Results - with Bound Ligands tab */}
                      {results.database === 'pdbaa' && results.pdb_hits.length > 0 && (
                        <PdbResultsWithTabs
                          hits={results.pdb_hits}
                          aggregatedLigands={results.aggregated_ligands || []}
                        />
                      )}

                      {/* SwissProt Results - with Reaction Participants tab */}
                      {results.database === 'swissprot' && results.swissprot_hits.length > 0 && (
                        <SwissprotResultsWithTabs
                          hits={results.swissprot_hits}
                          aggregatedChebi={results.aggregated_chebi || []}
                        />
                      )}

                      {/* CDD Results - with Functional Sites section */}
                      {results.database === 'Cdd' && results.cdd_domains.length > 0 && (
                        <CddResultsWithSites
                          domains={results.cdd_domains}
                          sites={results.cdd_sites || []}
                          queryLength={results.query_length}
                        />
                      )}
                    </>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      )}
    </div>
  )
}

// MIBiG Results Table - matches old blast2.pl print_blast_mibig layout
function MibigResultsTable({ hits }: { hits: BlastMibigHit[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b border-white/10">
            <th className="text-left py-2 px-3 text-gray-400 font-medium w-14">Hit</th>
            <th className="text-left py-2 px-3 text-gray-400 font-medium w-14">HSP</th>
            <th className="text-left py-2 px-3 text-gray-400 font-medium w-32">MIBiG accession</th>
            <th className="text-left py-2 px-3 text-gray-400 font-medium">Product</th>
            <th className="text-left py-2 px-3 text-gray-400 font-medium w-28">Protein ID</th>
            <th className="text-left py-2 px-3 text-gray-400 font-medium w-24">E-value</th>
            <th className="text-left py-2 px-3 text-gray-400 font-medium w-20">Identity</th>
            <th className="text-left py-2 px-3 text-gray-400 font-medium w-20">Cover</th>
            <th className="text-left py-2 px-3 text-gray-400 font-medium w-16">PDB</th>
            <th className="text-left py-2 px-3 text-gray-400 font-medium w-20">SwissProt</th>
            <th className="text-left py-2 px-3 text-gray-400 font-medium w-16">FUJISAN</th>
            <th className="text-left py-2 px-3 text-gray-400 font-medium w-16">UNKAI</th>
          </tr>
        </thead>
        <tbody>
          {hits.map((hit, idx) => {
            const rowClass = idx % 2 === 0 ? 'bg-white/[0.02]' : ''
            return (
              <tr
                key={idx}
                className={`border-b border-white/5 ${rowClass}`}
              >
                <td className="py-3 px-3 text-gray-500">{hit.hit_num}</td>
                <td className="py-3 px-3 text-gray-500">{hit.hsp_num || 1}</td>
                <td className="py-3 px-3">
                  <Link
                    to={`/clusters/${hit.mibig_accession}`}
                    className="font-mono text-accent-cyan hover:underline"
                  >
                    {hit.mibig_accession}
                  </Link>
                </td>
                <td className="py-3 px-3 text-gray-300 max-w-xs truncate" title={hit.product}>
                  <Link
                    to={`/proteins/${hit.mibig_accession}/${hit.protein_id}`}
                    state={{ from: location.pathname + location.search }}
                    className="hover:text-white hover:underline"
                  >
                    {hit.product || '-'}
                  </Link>
                </td>
                <td className="py-3 px-3">
                  <a
                    href={`https://www.ncbi.nlm.nih.gov/protein/${hit.protein_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-mono text-accent-purple hover:underline inline-flex items-center gap-1"
                  >
                    {hit.protein_id}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </td>
                <td className="py-3 px-3 font-mono text-gray-400 text-xs">
                  {hit.evalue.toExponential(2)}
                </td>
                <td className="py-3 px-3">
                  <Badge variant={hit.identity >= 90 ? 'green' : hit.identity >= 50 ? 'amber' : 'default'} size="sm">
                    {hit.identity.toFixed(1)}%
                  </Badge>
                </td>
                <td className="py-3 px-3 font-mono text-gray-400 text-xs">{hit.cover.toFixed(1)}%</td>
                <td className="py-3 px-3 text-right">
                  {hit.pdb_hit_count > 0 ? (
                    <Link
                      to={`/proteins/${hit.mibig_accession}/${hit.protein_id}/pdb-hits`}
                      state={{ from: location.pathname + location.search }}
                      className="text-accent-cyan hover:underline"
                    >
                      {hit.pdb_hit_count}
                    </Link>
                  ) : (
                    <span className="text-gray-500">0</span>
                  )}
                </td>
                <td className="py-3 px-3 text-right">
                  {hit.swissprot_hit_count > 0 ? (
                    <Link
                      to={`/proteins/${hit.mibig_accession}/${hit.protein_id}/swissprot-hits`}
                      state={{ from: location.pathname + location.search }}
                      className="text-accent-purple hover:underline"
                    >
                      {hit.swissprot_hit_count}
                    </Link>
                  ) : (
                    <span className="text-gray-500">0</span>
                  )}
                </td>
                <td className="py-3 px-3 text-right">
                  {hit.fujisan_hit_count > 0 ? (
                    <Link
                      to={`/proteins/${hit.mibig_accession}/${hit.protein_id}/fujisan-hits`}
                      state={{ from: location.pathname + location.search }}
                      className="text-orange-400 hover:underline"
                    >
                      {hit.fujisan_hit_count}
                    </Link>
                  ) : (
                    <span className="text-gray-500">0</span>
                  )}
                </td>
                <td className="py-3 px-3 text-right">
                  {hit.unkai_hit_count > 0 ? (
                    <Link
                      to={`/proteins/${hit.mibig_accession}/${hit.protein_id}/unkai-hits`}
                      state={{ from: location.pathname + location.search }}
                      className="text-emerald-400 hover:underline"
                    >
                      {hit.unkai_hit_count}
                    </Link>
                  ) : (
                    <span className="text-gray-500">0</span>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// PDB Results Table - matches old print_pdb layout
function PdbResultsTable({ hits }: { hits: BlastPdbHit[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b border-white/10">
            <th className="text-left py-2 px-3 text-gray-400 font-medium w-14">Hit</th>
            <th className="text-left py-2 px-3 text-gray-400 font-medium w-14">HSP</th>
            <th className="text-left py-2 px-3 text-gray-400 font-medium">Title</th>
            <th className="text-left py-2 px-3 text-gray-400 font-medium w-20">PDB ID</th>
            <th className="text-left py-2 px-3 text-gray-400 font-medium w-20">Chain ID</th>
            <th className="text-left py-2 px-3 text-gray-400 font-medium w-24">E-value</th>
            <th className="text-left py-2 px-3 text-gray-400 font-medium w-20">Identity</th>
            <th className="text-left py-2 px-3 text-gray-400 font-medium w-20">Cover</th>
            <th className="text-left py-2 px-3 text-gray-400 font-medium w-32">Ligand</th>
          </tr>
        </thead>
        <tbody>
          {hits.map((hit, idx) => {
            const rowClass = idx % 2 === 0 ? 'bg-white/[0.02]' : ''
            return (
              <tr
                key={idx}
                className={`border-b border-white/5 ${rowClass}`}
              >
                <td className="py-3 px-3 text-gray-500">{hit.hit_num}</td>
                <td className="py-3 px-3 text-gray-500">{hit.hsp_num || 1}</td>
                <td className="py-3 px-3 text-gray-300 max-w-md truncate" title={hit.title}>
                  {hit.title || '-'}
                </td>
                <td className="py-3 px-3">
                  <a
                    href={getPdbUrl(hit.pdb_id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-mono text-accent-cyan hover:underline inline-flex items-center gap-1"
                  >
                    {hit.pdb_id}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </td>
                <td className="py-3 px-3 text-gray-400 font-mono text-xs">{hit.chain_id || '-'}</td>
                <td className="py-3 px-3 font-mono text-gray-400 text-xs">
                  {hit.evalue.toExponential(2)}
                </td>
                <td className="py-3 px-3">
                  <Badge variant={hit.identity >= 90 ? 'green' : hit.identity >= 50 ? 'amber' : 'default'} size="sm">
                    {hit.identity.toFixed(1)}%
                  </Badge>
                </td>
                <td className="py-3 px-3 font-mono text-gray-400 text-xs">{hit.cover.toFixed(1)}%</td>
                <td className="py-3 px-3">
                  {hit.ligands && hit.ligands.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {hit.ligands.map((lig, i) => (
                        <a
                          key={i}
                          href={`https://www.rcsb.org/ligand/${lig}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-mono text-accent-amber hover:underline text-xs"
                        >
                          {lig}
                        </a>
                      ))}
                    </div>
                  ) : (
                    <span className="text-gray-500">-</span>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// SwissProt Results Table - matches old print_swissprot layout with reaction equations
function SwissprotResultsTable({ hits }: { hits: BlastSwissprotHit[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b border-white/10">
            <th className="text-left py-2 px-3 text-gray-400 font-medium w-14">Hit</th>
            <th className="text-left py-2 px-3 text-gray-400 font-medium w-14">HSP</th>
            <th className="text-left py-2 px-3 text-gray-400 font-medium">RecName</th>
            <th className="text-left py-2 px-3 text-gray-400 font-medium w-28">Accession</th>
            <th className="text-left py-2 px-3 text-gray-400 font-medium w-24">E-value</th>
            <th className="text-left py-2 px-3 text-gray-400 font-medium w-20">Identity</th>
            <th className="text-left py-2 px-3 text-gray-400 font-medium w-20">Cover</th>
            <th className="text-left py-2 px-3 text-gray-400 font-medium w-32">KEGG</th>
          </tr>
        </thead>
        <tbody>
          {hits.map((hit, idx) => {
            const rowClass = idx % 2 === 0 ? 'bg-white/[0.02]' : ''
            const hasReactions = hit.reactions && hit.reactions.length > 0
            // Calculate rowspan for hit/hsp columns (1 for main row + number of reaction rows)
            const rowSpan = hasReactions ? 1 + hit.reactions.length : 1
            return (
              <>
                {/* Main row */}
                <tr
                  key={`hit-${idx}`}
                  className={`border-b border-white/5 ${rowClass}`}
                >
                  <td className="py-3 px-3 text-gray-500" rowSpan={rowSpan}>{hit.hit_num}</td>
                  <td className="py-3 px-3 text-gray-500" rowSpan={rowSpan}>{hit.hsp_num || 1}</td>
                  <td className="py-3 px-3 text-gray-300 max-w-md truncate" title={hit.recname}>
                    {hit.recname || '-'}
                  </td>
                  <td className="py-3 px-3">
                    <a
                      href={getUniprotUrl(hit.swissprot_accession)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-mono text-accent-purple hover:underline inline-flex items-center gap-1"
                    >
                      {hit.swissprot_accession}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </td>
                  <td className="py-3 px-3 font-mono text-gray-400 text-xs" rowSpan={rowSpan}>
                    {hit.evalue.toExponential(2)}
                  </td>
                  <td className="py-3 px-3" rowSpan={rowSpan}>
                    <Badge variant={hit.identity >= 90 ? 'green' : hit.identity >= 50 ? 'amber' : 'default'} size="sm">
                      {hit.identity.toFixed(1)}%
                    </Badge>
                  </td>
                  <td className="py-3 px-3 font-mono text-gray-400 text-xs" rowSpan={rowSpan}>{hit.cover.toFixed(1)}%</td>
                  <td className="py-3 px-3">
                    {hit.kegg_ids && hit.kegg_ids.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {hit.kegg_ids.map((keggId, i) => (
                          <a
                            key={i}
                            href={`https://www.kegg.jp/entry/${keggId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-mono text-accent-cyan hover:underline text-xs"
                          >
                            {keggId}
                          </a>
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-500">-</span>
                    )}
                  </td>
                </tr>
                {/* Reaction equation rows (like old Perl print_swissprot) */}
                {hasReactions && hit.reactions.map((reaction, rIdx) => (
                  <tr key={`reaction-${idx}-${rIdx}`} className={rowClass}>
                    <td colSpan={6} className="py-2 px-3 text-gray-300 text-xs">
                      <div className="pl-4 border-l-2 border-accent-purple/30">
                        <span className="text-gray-400">{reaction.equation}</span>
                        {reaction.rhea_id && (
                          <span className="ml-2">
                            (<a
                              href={`https://www.rhea-db.org/rhea/${reaction.rhea_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-accent-cyan hover:underline"
                            >
                              RHEA:{reaction.rhea_id}
                            </a>)
                          </span>
                        )}
                        {reaction.ec_number && (
                          <div className="mt-1">
                            EC number:{' '}
                            <a
                              href={`https://enzyme.expasy.org/EC/${reaction.ec_number}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-accent-amber hover:underline"
                            >
                              {reaction.ec_number}
                            </a>
                          </div>
                        )}
                        {reaction.cofactors && reaction.cofactors.length > 0 && reaction.cofactors[0] && (
                          <div className="mt-1 text-gray-500">
                            Cofactor: {reaction.cofactors.filter(c => c).join(', ')}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// CDD Results Table with domain visualization
function CddResultsTable({ domains, queryLength }: { domains: BlastCddHit[]; queryLength: number }) {
  return (
    <div className="space-y-6">
      {/* Domain Visualization */}
      {queryLength > 0 && (
        <div className="bg-white/5 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-300 mb-3">Domain Architecture</h4>
          <div className="relative h-8 bg-white/10 rounded">
            {domains.map((domain, idx) => {
              const left = (domain.res_start / queryLength) * 100
              const width = ((domain.res_end - domain.res_start + 1) / queryLength) * 100
              const colors = ['bg-accent-cyan', 'bg-accent-purple', 'bg-accent-amber', 'bg-accent-green', 'bg-red-500']
              return (
                <div
                  key={idx}
                  className={`absolute h-full ${colors[idx % colors.length]} rounded opacity-80 hover:opacity-100 transition-opacity cursor-pointer`}
                  style={{ left: `${left}%`, width: `${Math.max(width, 1)}%` }}
                  title={`${domain.short_name} (${domain.res_start}-${domain.res_end})`}
                />
              )
            })}
          </div>
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>1</span>
            <span>{queryLength}</span>
          </div>
        </div>
      )}

      {/* Domain Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10">
              <th className="text-left py-3 px-4 text-gray-400 font-medium">PSSM ID</th>
              <th className="text-left py-3 px-4 text-gray-400 font-medium">Accession</th>
              <th className="text-left py-3 px-4 text-gray-400 font-medium">Short Name</th>
              <th className="text-left py-3 px-4 text-gray-400 font-medium">Description</th>
              <th className="text-left py-3 px-4 text-gray-400 font-medium">Range</th>
              <th className="text-left py-3 px-4 text-gray-400 font-medium">E-value</th>
              <th className="text-left py-3 px-4 text-gray-400 font-medium">Bitscore</th>
            </tr>
          </thead>
          <tbody>
            {domains.map((domain, idx) => (
              <motion.tr
                key={idx}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.03 }}
                className="border-b border-white/5 hover:bg-white/5"
              >
                <td className="py-3 px-4">
                  <a
                    href={getNcbiCddUrl(domain.pssm_id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-mono text-accent-cyan hover:underline inline-flex items-center gap-1"
                  >
                    {domain.pssm_id}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </td>
                <td className="py-3 px-4 text-gray-400 font-mono">{domain.accession}</td>
                <td className="py-3 px-4 text-accent-amber font-medium">{domain.short_name}</td>
                <td className="py-3 px-4 text-gray-300 max-w-xs truncate" title={domain.description}>
                  {domain.description || '-'}
                </td>
                <td className="py-3 px-4 text-gray-400 font-mono">
                  {domain.res_start}-{domain.res_end}
                </td>
                <td className="py-3 px-4 text-gray-400 font-mono">
                  {domain.evalue.toExponential(2)}
                </td>
                <td className="py-3 px-4 text-gray-400">{domain.bitscore.toFixed(1)}</td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// PDB Results with Tabs (PDB Entries + Bound Ligands) - matches old print_blast_pdb layout
function PdbResultsWithTabs({ hits, aggregatedLigands }: { hits: BlastPdbHit[]; aggregatedLigands: AggregatedLigand[] }) {
  const [activeTab, setActiveTab] = useState<'entries' | 'ligands'>('entries')

  return (
    <div className="space-y-4">
      {/* Tab Navigation */}
      <div className="flex border-b border-white/10">
        <button
          onClick={() => setActiveTab('entries')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'entries'
              ? 'border-accent-cyan text-accent-cyan'
              : 'border-transparent text-gray-400 hover:text-white'
          }`}
        >
          PDB Entries ({hits.length})
        </button>
        <button
          onClick={() => setActiveTab('ligands')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'ligands'
              ? 'border-accent-cyan text-accent-cyan'
              : 'border-transparent text-gray-400 hover:text-white'
          }`}
        >
          Bound Ligands ({aggregatedLigands.length})
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'entries' && <PdbResultsTable hits={hits} />}
      {activeTab === 'ligands' && <BoundLigandsTable ligands={aggregatedLigands} />}
    </div>
  )
}

// Bound Ligands Table - matches old print_ligand layout
function BoundLigandsTable({ ligands }: { ligands: AggregatedLigand[] }) {
  if (ligands.length === 0) {
    return <div className="text-center py-8 text-gray-500">No bound ligands found.</div>
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b border-white/10">
            <th className="text-left py-2 px-3 text-gray-400 font-medium w-20">Ligand</th>
            <th className="text-left py-2 px-3 text-gray-400 font-medium w-20">Count</th>
            <th className="text-left py-2 px-3 text-gray-400 font-medium w-28">Best E-value</th>
            <th className="text-left py-2 px-3 text-gray-400 font-medium">Name</th>
            <th className="text-left py-2 px-3 text-gray-400 font-medium w-32">Formula</th>
            <th className="text-left py-2 px-3 text-gray-400 font-medium w-24">PubChem</th>
            <th className="text-left py-2 px-3 text-gray-400 font-medium w-24">Structure</th>
          </tr>
        </thead>
        <tbody>
          {ligands.map((ligand, idx) => {
            const rowClass = idx % 2 === 0 ? 'bg-white/[0.02]' : ''
            const firstChar = ligand.ligand_id.charAt(0)
            return (
              <tr key={idx} className={`border-b border-white/5 ${rowClass}`}>
                <td className="py-3 px-3">
                  <a
                    href={`https://www.rcsb.org/ligand/${ligand.ligand_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-mono text-accent-cyan hover:underline inline-flex items-center gap-1"
                  >
                    {ligand.ligand_id}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </td>
                <td className="py-3 px-3 text-gray-300">{ligand.count}</td>
                <td className="py-3 px-3 font-mono text-gray-400 text-xs">
                  {ligand.best_evalue.toExponential(2)}
                </td>
                <td className="py-3 px-3 text-gray-300 max-w-xs truncate" title={ligand.name}>
                  {ligand.name || '-'}
                </td>
                <td className="py-3 px-3 text-gray-400 font-mono text-xs">
                  {ligand.formula || '-'}
                </td>
                <td className="py-3 px-3">
                  {ligand.pubchem_cid ? (
                    <a
                      href={`https://pubchem.ncbi.nlm.nih.gov/compound/${ligand.pubchem_cid}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-accent-purple hover:underline inline-flex items-center gap-1"
                    >
                      {ligand.pubchem_cid}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  ) : (
                    <span className="text-gray-500">-</span>
                  )}
                </td>
                <td className="py-3 px-3">
                  <a
                    href={`https://cdn.rcsb.org/images/ccd/unlabeled/${firstChar}/${ligand.ligand_id}.svg`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block"
                  >
                    <img
                      src={`https://cdn.rcsb.org/images/ccd/unlabeled/${firstChar}/${ligand.ligand_id}.svg`}
                      alt={ligand.ligand_id}
                      className="w-16 h-16 bg-white rounded p-1"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none'
                      }}
                    />
                  </a>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// SwissProt Results with Tabs (SwissProt Entries + Reaction Participants) - matches old print_blast_swissprot layout
function SwissprotResultsWithTabs({ hits, aggregatedChebi }: { hits: BlastSwissprotHit[]; aggregatedChebi: AggregatedChebi[] }) {
  const [activeTab, setActiveTab] = useState<'entries' | 'chebi'>('entries')

  return (
    <div className="space-y-4">
      {/* Tab Navigation */}
      <div className="flex border-b border-white/10">
        <button
          onClick={() => setActiveTab('entries')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'entries'
              ? 'border-accent-purple text-accent-purple'
              : 'border-transparent text-gray-400 hover:text-white'
          }`}
        >
          SwissProt Entries ({hits.length})
        </button>
        <button
          onClick={() => setActiveTab('chebi')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'chebi'
              ? 'border-accent-purple text-accent-purple'
              : 'border-transparent text-gray-400 hover:text-white'
          }`}
        >
          Reaction Participants ({aggregatedChebi.length})
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'entries' && <SwissprotResultsTable hits={hits} />}
      {activeTab === 'chebi' && <ReactionParticipantsTable chebiCompounds={aggregatedChebi} />}
    </div>
  )
}

// Reaction Participants Table - matches old print_chebi layout with substrate/product side
function ReactionParticipantsTable({ chebiCompounds }: { chebiCompounds: AggregatedChebi[] }) {
  if (chebiCompounds.length === 0) {
    return <div className="text-center py-8 text-gray-500">No reaction participants found.</div>
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b border-white/10">
            <th className="text-left py-2 px-3 text-gray-400 font-medium w-28">ChEBI ID</th>
            <th className="text-left py-2 px-3 text-gray-400 font-medium w-24">Role</th>
            <th className="text-left py-2 px-3 text-gray-400 font-medium w-20">Count</th>
            <th className="text-left py-2 px-3 text-gray-400 font-medium w-28">Best E-value</th>
            <th className="text-left py-2 px-3 text-gray-400 font-medium">Name</th>
            <th className="text-left py-2 px-3 text-gray-400 font-medium w-32">Formula</th>
            <th className="text-left py-2 px-3 text-gray-400 font-medium w-24">PubChem</th>
            <th className="text-left py-2 px-3 text-gray-400 font-medium w-24">Structure</th>
          </tr>
        </thead>
        <tbody>
          {chebiCompounds.map((compound, idx) => {
            const rowClass = idx % 2 === 0 ? 'bg-white/[0.02]' : ''
            // Extract numeric ID from CHEBI:12345 format
            const chebiNum = compound.chebi_id.replace('CHEBI:', '')
            // 0 = substrate (left side), 1 = product (right side)
            const role = compound.side === 0 ? 'Substrate' : 'Product'
            return (
              <tr key={idx} className={`border-b border-white/5 ${rowClass}`}>
                <td className="py-3 px-3">
                  <a
                    href={`https://www.ebi.ac.uk/chebi/searchId.do?chebiId=${chebiNum}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-mono text-accent-purple hover:underline inline-flex items-center gap-1"
                  >
                    {compound.chebi_id}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </td>
                <td className="py-3 px-3">
                  <Badge variant={compound.side === 0 ? 'cyan' : 'amber'} size="sm">
                    {role}
                  </Badge>
                </td>
                <td className="py-3 px-3 text-gray-300">{compound.count}</td>
                <td className="py-3 px-3 font-mono text-gray-400 text-xs">
                  {compound.best_evalue.toExponential(2)}
                </td>
                <td
                  className="py-3 px-3 text-gray-300 max-w-xs truncate"
                  title={compound.name}
                  dangerouslySetInnerHTML={{ __html: compound.name || '-' }}
                />
                <td className="py-3 px-3 text-gray-400 font-mono text-xs">
                  {compound.formula || '-'}
                </td>
                <td className="py-3 px-3">
                  {compound.pubchem_cid ? (
                    <a
                      href={`https://pubchem.ncbi.nlm.nih.gov/compound/${compound.pubchem_cid}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-accent-cyan hover:underline inline-flex items-center gap-1"
                    >
                      {compound.pubchem_cid}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  ) : (
                    <span className="text-gray-500">-</span>
                  )}
                </td>
                <td className="py-3 px-3">
                  <a
                    href={`https://www.rhea-db.org/chebi/${compound.chebi_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block"
                  >
                    <img
                      src={`https://www.rhea-db.org/chebi/${compound.chebi_id}`}
                      alt={compound.chebi_id}
                      className="w-16 h-16 bg-white rounded p-1"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none'
                      }}
                    />
                  </a>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// CDD Results with Functional Sites section - matches old print_blast_cdd layout
function CddResultsWithSites({ domains, sites, queryLength }: { domains: BlastCddHit[]; sites: BlastCddSite[]; queryLength: number }) {
  return (
    <div className="space-y-8">
      {/* Domain Results */}
      <CddResultsTable domains={domains} queryLength={queryLength} />

      {/* Functional Sites Section */}
      <div className="space-y-4">
        <h4 className="text-lg font-medium text-white">Functional Site List</h4>
        {sites.length === 0 ? (
          <div className="text-gray-500">No functional sites found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-2 px-3 text-gray-400 font-medium">Site Type</th>
                  <th className="text-left py-2 px-3 text-gray-400 font-medium">Residues</th>
                  <th className="text-left py-2 px-3 text-gray-400 font-medium w-40">Source Domain</th>
                </tr>
              </thead>
              <tbody>
                {sites.map((site, idx) => {
                  const rowClass = idx % 2 === 0 ? 'bg-white/[0.02]' : ''
                  return (
                    <tr key={idx} className={`border-b border-white/5 ${rowClass}`}>
                      <td className="py-3 px-3 text-gray-300">{site.site_type}</td>
                      <td className="py-3 px-3 text-gray-400 font-mono text-xs">
                        {site.residues.join(', ')}
                      </td>
                      <td className="py-3 px-3">
                        <a
                          href={`https://www.ncbi.nlm.nih.gov/Structure/cdd/cddsrv.cgi?uid=${site.source_domain}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-mono text-accent-cyan hover:underline inline-flex items-center gap-1"
                        >
                          {site.source_domain}
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
